import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { FileRejection, useDropzone } from "react-dropzone";
import { uuidv7 } from "uuidv7";
import {
    MAX_FINANCIAL_DOCUMENT_FILE_SIZE_BYTES,
    MAX_FINANCIAL_DOCUMENT_FILE_SIZE_MB,
} from "@common/constants/financial-documents";
import { formatExtensionsForDropzone } from "@common/helpers/financialDocument";
import { FetchFinancialDocumentsResponse } from "@common/dto/financialDocument/fetch-financial-documents-response.dto";
import { TransactionsImportStatus } from "@common/types/financialDocument";
import { useScope } from "@state/scopeContext.lib";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useFinancialDocumentsExtendedQuery } from "@components/documents/useFinancialDocumentsExtendedQuery";
import { accrualImporterDetailIncludes } from "@components/documents/financialDocumentDetailIncludes";
import { invalidateInvoiceQueries } from "@api/invoice.api";
import { invalidateBillQueries } from "@api/bill.api";
import { ImporterToast } from "@components/importer/ImporterToast";
import { IMPORTER_TOAST_KEY } from "@components/importer/ImporterToast.lib";
import { isImportRunning } from "@components/importer/helpers";
import importerStyles from "@components/importer/ImporterPage.module.scss";
import {
    ACCRUAL_DOCUMENT_FILE_TYPES,
    ACCRUAL_DOCUMENT_FILE_TYPES_NAMES,
} from "./accrualDocumentImporter.lib";
import { AccrualImporterContext } from "./accrualImporter.context";
import {
    accrualDocumentBeingProcessed,
    isAccrualRowReadyForImport,
} from "./accrualImporter.helpers";
import { useAccrualImporterUploadMutation } from "./useAccrualImporterUpload";
import { AccrualImporterPageStatus } from "./AccrualImporterPageStatus";
import { AccrualImporterPageContent } from "./AccrualImporterPageContent";
import { AccrualImporterPageFooter } from "./AccrualImporterPageFooter";
import { AccrualDocumentReviewDrawer } from "./AccrualDocumentReviewDrawer";
import { useConfirmAccrualDocuments } from "./useConfirmAccrualDocuments";

const sizeSuffix = `of size less than ${MAX_FINANCIAL_DOCUMENT_FILE_SIZE_MB} MB are allowed.`;
const errorMessage = `Only ${ACCRUAL_DOCUMENT_FILE_TYPES_NAMES} ${sizeSuffix}`;

interface Props {
    onHide: () => void;
}

export const AccrualDocumentImporter: React.FC<Props> = ({ onHide }) => {
    const {
        mode,
        importerQueue,
        documentRows,
        upsertDocumentRow,
        removeDocumentRow,
        isRefreshEnabled,
    } = useContext(AccrualImporterContext);
    const toaster = useToaster();
    const { workspaceId: activeWorkspaceId } = useScope();
    const pendingImportDocumentIdsRef = useRef<Set<number>>(new Set());
    const notifiedFailureDocumentIdsRef = useRef<Set<number>>(new Set());

    const upload = useAccrualImporterUploadMutation({
        onSuccess: () => {
            toaster.toast(<ImporterToast subject={mode} />, IMPORTER_TOAST_KEY);
        },
        onUploadError: (file) => {
            toaster.toast(`Failed to upload ${file.name}`);
        },
    });

    const rowsReadyForImport = useMemo(
        () => documentRows.filter(isAccrualRowReadyForImport),
        [documentRows],
    );

    const hasImportsRunning = useMemo(
        () =>
            documentRows.some(
                (row) =>
                    row.financialDocument &&
                    isImportRunning(row.financialDocument),
            ),
        [documentRows],
    );

    const canCloseImporter = useMemo(
        () =>
            documentRows.length > 0 &&
            documentRows.every(
                (row) =>
                    row.financialDocument &&
                    !accrualDocumentBeingProcessed(row.financialDocument),
            ),
        [documentRows],
    );

    const handleDocumentsRemovedFromImporter = useCallback(
        (importerDocuments: FetchFinancialDocumentsResponse) => {
            const returnedDocumentIds = importerDocuments.data.map(
                (document) => document.id,
            );
            const documentRowsToClear = documentRows.filter(
                (row) =>
                    row.financialDocument &&
                    !returnedDocumentIds.includes(row.financialDocument.id),
            );

            let accrualListsRefreshRequired = false;

            documentRowsToClear.forEach((row) => {
                const financialDocument = row.financialDocument;
                if (!financialDocument) {
                    return;
                }

                const documentId = financialDocument.id;
                const importStatus = financialDocument.transactionsImportStatus;

                if (importStatus === TransactionsImportStatus.FAILED) {
                    return;
                }

                pendingImportDocumentIdsRef.current.delete(documentId);
                notifiedFailureDocumentIdsRef.current.delete(documentId);

                if (
                    importStatus === TransactionsImportStatus.FINISHED ||
                    importStatus === TransactionsImportStatus.IN_PROGRESS ||
                    importStatus === TransactionsImportStatus.SCHEDULED
                ) {
                    upsertDocumentRow({
                        ...row,
                        financialDocument: {
                            ...financialDocument,
                            transactionsImportStatus:
                                TransactionsImportStatus.FINISHED,
                        },
                    });
                    if (importStatus !== TransactionsImportStatus.FINISHED) {
                        accrualListsRefreshRequired = true;
                    }
                } else {
                    removeDocumentRow(row);
                }
            });

            if (accrualListsRefreshRequired) {
                if (mode === "invoices") {
                    invalidateInvoiceQueries();
                } else {
                    invalidateBillQueries();
                }
            }
        },
        [documentRows, mode, removeDocumentRow, upsertDocumentRow],
    );

    const shouldPollImporterDocuments = useCallback(
        (data: FetchFinancialDocumentsResponse | undefined) => {
            if (!isRefreshEnabled) {
                return false;
            }

            const hasProcessingDocumentRow = documentRows.some(
                (row) =>
                    row.financialDocument &&
                    accrualDocumentBeingProcessed(row.financialDocument),
            );

            const hasProcessingServerDocument = (data?.data ?? []).some(
                accrualDocumentBeingProcessed,
            );

            const hasRunningImportServerDocument = (data?.data ?? []).some(
                isImportRunning,
            );

            return (
                hasProcessingDocumentRow ||
                hasProcessingServerDocument ||
                hasImportsRunning ||
                hasRunningImportServerDocument
            );
        },
        [documentRows, hasImportsRunning, isRefreshEnabled],
    );

    const { data: importerDocuments } = useFinancialDocumentsExtendedQuery({
        queryParams: {
            workspaceId: activeWorkspaceId ?? "",
            filters: {
                importerQueue,
            },
            limit: 0,
            page: 0,
        },
        detailIncludes: accrualImporterDetailIncludes,
        additionalParams: { enabled: !!activeWorkspaceId },
        onSuccess: handleDocumentsRemovedFromImporter,
        shouldRefetch: shouldPollImporterDocuments,
    });

    const importerDocumentData = importerDocuments?.data;

    useEffect(() => {
        importerDocumentData?.forEach((document) => {
            upsertDocumentRow({
                id: document.id.toString(),
                financialDocument: document,
            });
        });
    }, [importerDocumentData, upsertDocumentRow]);

    useEffect(() => {
        for (const row of documentRows) {
            const financialDocument = row.financialDocument;
            if (!financialDocument) {
                continue;
            }

            if (
                financialDocument.transactionsImportStatus !==
                TransactionsImportStatus.FAILED
            ) {
                continue;
            }

            if (
                !pendingImportDocumentIdsRef.current.has(financialDocument.id)
            ) {
                continue;
            }

            if (
                notifiedFailureDocumentIdsRef.current.has(financialDocument.id)
            ) {
                continue;
            }

            notifiedFailureDocumentIdsRef.current.add(financialDocument.id);
            pendingImportDocumentIdsRef.current.delete(financialDocument.id);
            toaster.toast(
                `Failed to import ${financialDocument.fileName}. Review and try again.`,
            );
        }
    }, [documentRows, toaster]);

    const handleImportStarted = useCallback(
        (financialDocumentIds: number[]) => {
            financialDocumentIds.forEach((documentId) => {
                pendingImportDocumentIdsRef.current.add(documentId);
                notifiedFailureDocumentIdsRef.current.delete(documentId);

                const row = documentRows.find(
                    (documentRow) =>
                        documentRow.financialDocument?.id === documentId,
                );
                if (row?.financialDocument) {
                    upsertDocumentRow({
                        ...row,
                        financialDocument: {
                            ...row.financialDocument,
                            transactionsImportStatus:
                                TransactionsImportStatus.SCHEDULED,
                        },
                    });
                }
            });
            toaster.toast(
                <ImporterToast subject={mode} verb="Creating" />,
                IMPORTER_TOAST_KEY,
            );
        },
        [documentRows, mode, toaster, upsertDocumentRow],
    );

    const handleImportError = useCallback(
        (financialDocumentIds: number[], error: Error) => {
            financialDocumentIds.forEach((documentId) => {
                pendingImportDocumentIdsRef.current.delete(documentId);
            });
            toaster.toast(`Failed to start import: ${error.message}`);
        },
        [toaster],
    );

    const { confirmDocuments, isConfirming } = useConfirmAccrualDocuments({
        onImportStarted: handleImportStarted,
        onImportError: handleImportError,
    });

    const onDrop = useCallback(
        (acceptedFiles: File[], fileRejections: FileRejection[]) => {
            for (const file of acceptedFiles) {
                const id = uuidv7();
                upsertDocumentRow({
                    id,
                    file,
                });
                upload.mutate({
                    file,
                    documentId: id,
                });
            }

            if (fileRejections.length === 1) {
                toaster.toast(errorMessage);
            }

            if (fileRejections.length > 1) {
                toaster.toast(
                    `${fileRejections.length} files rejected. ${errorMessage}`,
                );
            }
        },
        [upsertDocumentRow, toaster, upload],
    );

    const acceptedFileTypes = useMemo(
        () => formatExtensionsForDropzone(ACCRUAL_DOCUMENT_FILE_TYPES),
        [],
    );

    const {
        getRootProps,
        getInputProps,
        open: openUploadPrompt,
    } = useDropzone({
        onDrop,
        accept: acceptedFileTypes,
        maxSize: MAX_FINANCIAL_DOCUMENT_FILE_SIZE_BYTES,
        noClick: true,
        noKeyboard: true,
    });

    return (
        <>
            <div className={importerStyles.container} {...getRootProps()}>
                <AccrualImporterPageStatus />
                <AccrualImporterPageContent />
                <input {...getInputProps()} />
            </div>
            <AccrualImporterPageFooter
                rowsReadyForImport={rowsReadyForImport}
                hasImportsRunning={hasImportsRunning}
                canClose={canCloseImporter}
                onHide={onHide}
                openUploadPrompt={openUploadPrompt}
                confirmDocuments={confirmDocuments}
                isConfirming={isConfirming}
            />
            <AccrualDocumentReviewDrawer />
        </>
    );
};
